import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, MessageCircle } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import { useVisibleLoading } from '../../hooks/useVisibleLoading.js';
import Modal from '../../components/ui/Modal.jsx';
import PremiumCard from '../../components/ui/PremiumCard.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import PublicPageHero from '../../components/public/PublicPageHero.jsx';
import publicCta from '../../content/publicCta.json';
import { groupConveniosByCategory } from '../../constants/convenioCategories.js';
import { getConvenioWhatsAppHref } from '../../utils/convenioWhatsApp.js';

const DESC_FALLBACK = 'Consulta condiciones y vigencia con la cooperativa.';
const DESC_PREVIEW_MAX = 140;

function getDescripcionPreview(descripcion) {
  const text = (descripcion || '').trim() || DESC_FALLBACK;
  const hasRealText = Boolean((descripcion || '').trim());
  if (!hasRealText || text.length <= DESC_PREVIEW_MAX) {
    return { preview: text, needsMore: false };
  }
  return {
    preview: `${text.slice(0, DESC_PREVIEW_MAX).trimEnd()}…`,
    needsMore: true,
  };
}

function ConvenioCard({ c, delayMs, onVerMas }) {
  const waHref = getConvenioWhatsAppHref(c);
  const { preview, needsMore } = getDescripcionPreview(c.descripcion);

  return (
    <ScrollReveal delayMs={delayMs} className="min-w-0">
      <PremiumCard hover className="flex h-full flex-col overflow-hidden !p-0">
        <div className="relative flex aspect-[16/10] items-center justify-center bg-white p-4 ring-1 ring-slate-200">
          {c.imagen ? (
            <img
              src={assetUrl(c.imagen)}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <Handshake className="h-10 w-10 text-accent-lime" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wider">Convenio</span>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-6">
          <h3 className="break-words font-display text-xl font-semibold text-white">{c.nombre}</h3>
          <p className="mt-3 flex-1 break-words text-justify text-sm leading-relaxed text-slate-400">
            {preview}
          </p>
          {needsMore ? (
            <button
              type="button"
              onClick={() => onVerMas(c)}
              className="mt-2 self-start text-sm font-semibold text-accent-lime transition hover:text-white"
            >
              Ver más
            </button>
          ) : null}
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {publicCta.conveniosWhatsAppLabel}
            </a>
          ) : null}
        </div>
      </PremiumCard>
    </ScrollReveal>
  );
}

export default function ConveniosPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const showLoading = useVisibleLoading(loading);

  const sections = useMemo(() => groupConveniosByCategory(items), [items]);
  const selectedWaHref = selected ? getConvenioWhatsAppHref(selected) : null;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/convenios');
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar los convenios');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-[60vh] pb-16 lg:pb-24">
      <PublicPageHero
        eyebrow="Beneficios"
        title="Convenios para asociados"
        description="Consulta los convenios vigentes por categoría: recreación, salud y seguridad. Escríbenos por WhatsApp para más detalles."
      />

      <section className="border-b border-slate-200 bg-white py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {error && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          <div className={error ? 'mt-10' : ''}>
            {showLoading && (
              <BrandLoading variant="inline" tone="light" label="Cargando convenios…" className="min-h-[40vh]" />
            )}

            {!showLoading && !error && items.length === 0 && (
              <p className="text-center text-slate-600">No hay convenios publicados por ahora.</p>
            )}

            {!showLoading && sections.length > 0 && (
              <div className="space-y-16">
                {sections.map((section, si) => (
                  <div key={section.id}>
                    <ScrollReveal>
                      <div className="max-w-3xl">
                        <h2 className="font-display text-2xl font-bold text-accent-blue md:text-3xl">
                          {section.label}
                        </h2>
                        <p className="mt-3 text-base text-slate-600 md:text-lg">{section.description}</p>
                      </div>
                    </ScrollReveal>
                    <div className="mt-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                      {section.convenios.map((c, i) => (
                        <ConvenioCard
                          key={c.id}
                          c={c}
                          delayMs={Math.min(si * 40 + i, 8) * 45}
                          onVerMas={setSelected}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!showLoading && (
            <p className="mt-14 text-center text-sm text-slate-600">
              ¿No encuentras el convenio que buscas?{' '}
              <Link to="/contacto" className="font-semibold text-accent-lime hover:text-[#0d2b7f]">
                Contáctanos
              </Link>{' '}
              y te orientamos.
            </p>
          )}
        </div>
      </section>

      <Modal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.nombre || 'Convenio'}
      >
        {selected ? (
          <div className="space-y-5">
            {selected.imagen ? (
              <div className="flex max-h-44 items-center justify-center rounded-xl bg-white p-4">
                <img
                  src={assetUrl(selected.imagen)}
                  alt=""
                  className="max-h-36 max-w-full object-contain"
                />
              </div>
            ) : null}
            <p className="whitespace-pre-wrap break-words text-base leading-relaxed text-slate-200">
              {(selected.descripcion || '').trim() || DESC_FALLBACK}
            </p>
            {selectedWaHref ? (
              <a
                href={selectedWaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />
                {publicCta.conveniosWhatsAppLabel}
              </a>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
